import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../styles/Sidebar.css';
import { useMobile } from '../hooks/useMobile';
// FEEDBACK_WIDGET
import FeedbackWidget from './FeedbackWidget';
// END FEEDBACK_WIDGET
import { BrokerLevelInline } from './BrokerLevelBadge';

const token = () => localStorage.getItem('token');

const DARK = '#1a1a2e';
const GREEN = '#16c784';

export default function Sidebar() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const user      = JSON.parse(localStorage.getItem('user') || '{}');
  const [mobileOpen, setMobileOpen] = useState(false);
  const isMobile = useMobile();
  const [pendingLeads, setPendingLeads] = useState(0);
  const [pendingAgreements, setPendingAgreements] = useState(0);
  const [hasPartners, setHasPartners] = useState(false);
  const [hasBackbook, setHasBackbook] = useState(false);
  const [navModules, setNavModules] = useState({ leads: true, deals: true, introducers: true, clients: true, analytics: true, announcements: true, scheduling: false });

  const isPlatformOwner = user.role === 'platform_owner';
  const isIntroducer    = user.role === 'introducer';
  const isBroker        = !isPlatformOwner && !isIntroducer;

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!isBroker) return;
    // Check if company has any enabled partners (controls Referrals visibility)
    fetch('/api/partners/available', { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => r.ok ? r.json() : [])
      .then(d => setHasPartners(Array.isArray(d) && d.length > 0))
      .catch(() => {});
    // Check if company has Back Book enabled (controls Back Book nav visibility)
    fetch('/api/backbook/status', { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => r.ok ? r.json() : { enabled: false })
      .then(d => setHasBackbook(!!d.enabled))
      .catch(() => {});
    // Check which of Leads/Deals/Introducers this company has switched on (14 Aug
    // 2026 — Alegra: back-book-only client, real per-company module toggle). Defaults
    // to all-on (fail open in the UI) so a network hiccup never wrongly hides a nav
    // item the company actually has — the API itself still enforces the real block.
    fetch('/api/company-profile/nav-modules', { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => r.ok ? r.json() : { nav_modules: {} })
      .then(d => setNavModules({ leads: true, deals: true, introducers: true, clients: true, analytics: true, announcements: true, scheduling: false, ...d.nav_modules }))
      .catch(() => {});
    const fetchAgreements = async () => {
      try {
        const res = await fetch('/api/invites', { headers: { Authorization: `Bearer ${token()}` } });
        if (!res.ok) return;
        const data = await res.json();
        setPendingAgreements(data.filter(i => i.agreement_status === 'pending').length);
      } catch {}
    };
    fetchAgreements();
    const iv2 = setInterval(fetchAgreements, 60000);
    const fetchCount = async () => {
      try {
        const res = await fetch('/api/introducer-leads/pending', {
          headers: { Authorization: `Bearer ${token()}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        setPendingLeads(data.count || 0);
      } catch {}
    };
    fetchCount();
    const iv = setInterval(fetchCount, 60000);
    return () => { clearInterval(iv2); clearInterval(iv); };
  }, [isBroker]);

  const ownerMenu = [
    { label: 'Dashboard',         path: '/owner',                       icon: '📊' },
    { label: 'Companies',         path: '/owner/companies',             icon: '🏢' },
    { label: 'Onboard Company',   path: '/owner/onboarding',            icon: '➕' },
    { label: 'Users',             path: '/owner/users',                 icon: '👥' },
    { label: 'Introducers',       path: '/owner/introducers',           icon: '🤝' },
    { label: 'Impersonate User', path: '/admin/impersonate',      icon: '👤' },
    { label: 'Agreements',        path: '/owner/agreement-tracker',     icon: '📋' },
    { label: 'Commission Audit',  path: '/owner/commission-audit',      icon: '💰' },
    { label: 'Funnel Analytics',  path: '/owner/funnel-analytics',      icon: '🔻' },
    { label: 'Client Documents',  path: '/owner/client-documents',      icon: '📁' },
    { label: 'Pipeline',          path: '/owner/prospects',             icon: '🎯' },
    { label: 'Partners',          path: '/owner/partners',              icon: '🤝' },
    { label: 'Enquiries',         path: '/owner/enquiries',             icon: '📩' },
    { label: 'Client Lookup',      path: '/owner/clients',               icon: '👤' },
    { label: 'Audit Log',         path: '/owner/audit-log',             icon: '🔍' },
    { label: 'Role Labels',       path: '/owner/role-labels',           icon: '🏷' },
    // FEEDBACK_WIDGET
    { label: 'Feedback Inbox', path: '/owner/feedback', icon: '🧪' },
    // END FEEDBACK_WIDGET
    { label: 'Email Templates',   path: '/owner/email-templates',       icon: '✉️' },
    { label: 'Analytics',         path: '/owner/analytics',             icon: '📈' },
  ];
  const isBrokerOwner   = user.role === 'broker' && ['owner','admin'].includes(user.brokerLevel);
  const isBrokerManager = user.role === 'broker' && user.brokerLevel === 'manager';

  const brokerMenu = [
    { label: 'Dashboard',   path: '/dashboard',            icon: '📊' },
    ...(navModules.leads !== false ? [{ label: 'Leads', path: '/leads', icon: '📥', badge: pendingLeads }] : []),
    ...(navModules.deals !== false ? [{ label: 'Deals', path: '/deals', icon: '💼' }] : []),
    ...(navModules.clients !== false ? [{ label: 'Clients', path: '/clients', icon: '👪' }] : []),
    ...(hasBackbook ? [{ label: 'Back Book', path: '/backbook', icon: '📖' }] : []),
    ...(navModules.introducers !== false ? [{ label: 'Introducers', path: '/introducers', icon: '🤝', badge: pendingAgreements }] : []),
    ...(hasPartners ? [{ label: 'Partner Referrals', path: '/referrals', icon: '🤝' }] : []),
    ...(isBrokerOwner && navModules.announcements !== false ? [{ label: 'Announcements', path: '/broker/announcements', icon: '📢' }] : []),
    ...((isBrokerOwner || isBrokerManager) ? [{ label: 'Team',      path: '/team',           icon: '👥' }] : []),
    ...(navModules.scheduling && (isBrokerOwner || isBrokerManager) ? [{ label: 'Team Calendar', path: '/team-calendar', icon: '🗓️' }] : []),
    ...(navModules.scheduling && isBrokerOwner ? [{ label: 'Booking Page', path: '/booking-setup', icon: '🔗' }] : []),
    ...(navModules.analytics !== false ? [{ label: 'Analytics', path: '/analytics', icon: '📈' }] : []),
    ...(isBrokerOwner         ? [{ label: 'Billing',    path: '/billing',        icon: '💳' }] : []),
    { label: 'Settings',    path: '/settings',             icon: '⚙️' },
    ...(user?.company?.widget_enabled ? [{ label: 'Web Widget', path: '/admin/widget', icon: '🌐' }] : []),
  ];
  const intLevel = user.introducerLevel || user.introducer_level;
  const introducerMenu = [
    { label: 'Dashboard',      path: '/introducer-dashboard',      icon: '📊' },
    { label: 'Refer a Client', path: '/submit-lead',               icon: '📝' },
    { label: 'My Referrals',   path: '/introducer/referrals',      icon: '🔗' },
    { label: 'Commissions',    path: '/introducer/commissions',    icon: '💰' },
    { label: 'Team & Users',   path: '/dashboard/introducer-team', icon: '👥' },
    { label: 'Analytics',      path: '/introducer-analytics',      icon: '📈' },
    { label: 'Clients',       path: '/introducer/clients',          icon: '👤' },
    { label: 'About Broker',   path: '/about-broker',              icon: '🏢' },
    { label: 'Settings',       path: '/settings',                  icon: '⚙️' },
  ];

  const menu     = isPlatformOwner ? ownerMenu : isIntroducer ? introducerMenu : brokerMenu;
  const subtitle = isPlatformOwner ? 'Platform' : isIntroducer ? 'Introducer' : 'Broker';

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const handleNav = (path) => {
    if (path !== '#') navigate(path);
    setMobileOpen(false);
  };

  const isActive = (path) => path !== '#' && location.pathname === path;

  return (
    <>
      {/* ── Desktop sidebar (CSS-driven, unchanged) ── */}
      <div className="sidebar">
        <div className="sidebar-header">
          <h2 className="sidebar-logo">Lead Manager</h2>
          <p className="sidebar-subtitle">{subtitle}</p>
        </div>
        <div className="sidebar-user">
          <div className="user-avatar">{user.name?.charAt(0).toUpperCase()}</div>
          <div className="user-details">
            <div className="user-name">{user.name}</div>
            <BrokerLevelInline user={user} />
          </div>
        </div>
        <nav className="sidebar-nav">
          {menu.map((item) => (
            <button
              key={item.path + item.label}
              onClick={() => handleNav(item.path)}
              className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
              style={{ position: 'relative' }}
            >
              <span className="nav-icon">{item.icon}</span>
              <span className="nav-label">{item.label}</span>
              {item.badge > 0 && (
                <span style={{
                  position:'absolute',top:6,right:8,minWidth:18,height:18,
                  borderRadius:9,background:'#ef4444',color:'#fff',fontSize:10,
                  fontWeight:700,display:'flex',alignItems:'center',
                  justifyContent:'center',padding:'0 4px',
                }}>
                  {item.badge > 99 ? '99+' : item.badge}
                </span>
              )}
            </button>
          ))}
        </nav>
        <div className="sidebar-footer">
          <button onClick={handleLogout} className="logout-btn">
            <span className="nav-icon">🚪</span>
            <span className="nav-label">Logout</span>
          </button>
        </div>
      </div>

      {/* ── Mobile UI (all inline styles — not purgeable) ── */}
      {isMobile && (
        <>
          {/* Top bar — replaces the old floating hamburger */}
          <div style={{
            position:'fixed', top:0, left:0, right:0, height:52,
            background: DARK, borderBottom:`2px solid ${GREEN}`,
            zIndex:3000, display:'flex', alignItems:'center',
            padding:'0 12px', gap:12,
            boxShadow:'0 2px 8px rgba(0,0,0,0.3)',
          }}>
            <button
              onClick={() => setMobileOpen(o => !o)}
              aria-label="Open menu"
              style={{
                background:'none', border:'none', color:GREEN,
                fontSize:22, cursor:'pointer', padding:'6px 8px',
                display:'flex', alignItems:'center', justifyContent:'center',
                borderRadius:6, flexShrink:0,
              }}
            >
              {mobileOpen ? '✕' : '☰'}
            </button>
            <span style={{ flex:1, color:'white', fontWeight:700, fontSize:15 }}>
              Lead Manager
            </span>
            <div style={{
              width:32, height:32, borderRadius:'50%',
              background:GREEN, color:'white',
              display:'flex', alignItems:'center', justifyContent:'center',
              fontWeight:'bold', fontSize:14, flexShrink:0,
            }}>
              {user.name?.charAt(0).toUpperCase()}
            </div>
          </div>

          {/* Backdrop */}
          {mobileOpen && (
            <div
              onClick={() => setMobileOpen(false)}
              style={{
                position:'fixed', inset:0,
                background:'rgba(0,0,0,0.55)',
                zIndex:2500,
              }}
            />
          )}

          {/* Drawer — starts below the top bar */}
          <div style={{
            position:'fixed', top:52, left:0,
            width:260, height:'calc(100vh - 52px)',
            background: DARK,
            zIndex:2800,
            transform: mobileOpen ? 'translateX(0)' : 'translateX(-100%)',
            transition:'transform 0.25s ease',
            display:'flex', flexDirection:'column',
            boxShadow:'4px 0 16px rgba(0,0,0,0.5)',
            overflowY:'auto',
          }}>
            {/* Drawer header — name + role */}
            <div style={{
              padding:'14px 16px 12px', borderBottom:`1px solid rgba(255,255,255,0.1)`,
              display:'flex', alignItems:'center', gap:12,
            }}>
              <div style={{
                width:36, height:36, borderRadius:'50%',
                background:GREEN, color:'white',
                display:'flex', alignItems:'center', justifyContent:'center',
                fontWeight:'bold', fontSize:15, flexShrink:0,
              }}>
                {user.name?.charAt(0).toUpperCase()}
              </div>
              <div>
                <div style={{ fontSize:14, fontWeight:'bold', color:'white' }}>{user.name}</div>
                <div style={{ fontSize:11, color:'#aaa', textTransform:'capitalize' }}>{subtitle}</div>
              </div>
            </div>

            {/* Nav */}
            <nav style={{ flex:1, padding:'8px 0' }}>
              {menu.map((item) => (
                <button
                  key={item.path + item.label}
                  onClick={() => handleNav(item.path)}
                  style={{
                    width:'100%', padding:'14px 20px',
                    background: isActive(item.path) ? 'rgba(22,199,132,0.2)' : 'none',
                    borderLeft: isActive(item.path) ? `3px solid ${GREEN}` : '3px solid transparent',
                    color:'white', cursor:'pointer',
                    display:'flex', alignItems:'center', gap:14,
                    fontSize:15, textAlign:'left',
                    fontWeight: isActive(item.path) ? 'bold' : 'normal',
                    position:'relative',
                  }}
                >
                  <span style={{ fontSize:20, width:24, textAlign:'center' }}>{item.icon}</span>
                  <span>{item.label}</span>
                  {item.badge > 0 && (
                    <span style={{
                      marginLeft:'auto', minWidth:20, height:20,
                      borderRadius:10, background:'#ef4444', color:'white',
                      fontSize:11, fontWeight:700,
                      display:'flex', alignItems:'center', justifyContent:'center',
                      padding:'0 5px',
                    }}>
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                </button>
              ))}
            </nav>

            {/* Logout */}
            <div style={{ borderTop:'1px solid rgba(255,255,255,0.1)', padding:'8px 0' }}>
              <button
                onClick={handleLogout}
                style={{
                  width:'100%', padding:'14px 20px',
                  background:'none', border:'none',
                  borderLeft:'3px solid transparent',
                  color:'white', cursor:'pointer',
                  display:'flex', alignItems:'center', gap:14, fontSize:15,
                }}
              >
                <span style={{ fontSize:20, width:24, textAlign:'center' }}>🚪</span>
                <span>Logout</span>
              </button>
            </div>
          </div>
        </>
      )}
        {/* FEEDBACK_WIDGET */}
        <FeedbackWidget />
        {/* END FEEDBACK_WIDGET */}
    </>
  );
}
